import { useEffect, useRef, useState } from 'react';
import axios, { AxiosError } from 'axios';
import useDebounce from 'hooks/useDebounce';
import axiosInstance from 'utils/axiosInterceptors';
import { SelectOption } from './CreatePurchaseOrderForm';

type ProductTypeaheadItem = {
  productId: number;
  name: string;
  description: string;
  productCategoryName: string;
};

const MIN_SEARCH_LENGTH = 2;

const isCanceled = (error: unknown) =>
  axios.isCancel(error) ||
  (error instanceof AxiosError && error.code === 'ERR_CANCELED');

const toOption = (item: ProductTypeaheadItem): SelectOption => ({
  value: item.productId,
  label: `${item.name} - ${item.description} (${item.productCategoryName})`
});

export const useProductTypeahead = () => {
  const [input, setInput] = useState('');
  const debouncedInput = useDebounce(input, 300);
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const search = debouncedInput.trim();
    abortRef.current?.abort();

    if (search.length < MIN_SEARCH_LENGTH) {
      setIsLoading(false);
      setOptions([]);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    const fetchOptions = async () => {
      setIsLoading(true);
      try {
        const res = await axiosInstance.get<ProductTypeaheadItem[]>(
          '/api/v1/products/typeahead',
          {
            params: { search, take: 15 },
            signal: controller.signal
          }
        );
        setOptions(res.data.map(toOption));
      } catch (error) {
        if (isCanceled(error)) {
          return;
        }
        console.error(error);
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    void fetchOptions();
    return () => controller.abort();
  }, [debouncedInput]);

  return {
    options,
    isLoading,
    handleSearchChange: setInput,
    noOptionsMessage: ({ inputValue }: { inputValue: string }) =>
      inputValue.trim().length < MIN_SEARCH_LENGTH
        ? 'Type at least 2 characters'
        : 'No products found'
  };
};
